// ─── Data utilities, normalization, migration, and computation ────────────────
import { todayStr, daysBetween } from "./dates";

// ─── Storage versioning ───────────────────────────────────────────────────────
export const CURRENT_KEY = "pl_business_data_v2";
export const LEGACY_KEYS = ["pl_business_data"]; // all old keys to migrate from
export const SCHEMA_VERSION = 2;

export const defaultState = {
  _version: SCHEMA_VERSION,
  openingBalances: [],
  purchases: [],
  expenses: [],
  sales: [],
  salaries: [],
  debts: [],
};

// ─── Constants ────────────────────────────────────────────────────────────────
export const TABS = [
  "Dashboard",
  "Daily P&L",
  "Purchases",
  "Expenses",
  "Sales",
  "Salaries",
  "Debt Manager",
  "Ledger",
  "Settings",
];

export const EXPENSE_CATS = [
  "Raw Material",
  "Packaging",
  "Transport / Freight",
  "Utilities",
  "Office Supplies",
  "Repairs & Maintenance",
  "Fuel",
  "Miscellaneous",
];

// ─── Normalize records from any version into current shape ────────────────────
export function normalizePurchase(p) {
  const purchaseDate = p.purchaseDate || "";
  const payDue =
    p.payDue ||
    (purchaseDate
      ? (() => {
          const d = new Date(purchaseDate);
          d.setDate(d.getDate() + 1);
          return d.toISOString().split("T")[0];
        })()
      : "");
  return {
    id: p.id || Date.now(),
    vendor: p.vendor || "",
    item: p.item || "",
    amount: parseFloat(p.amount) || 0,
    purchaseDate,
    payDue,
    paid: !!p.paid,
    paidDate: p.paidDate || "",
  };
}

export function normalizeExpense(e) {
  return {
    id: e.id || Date.now(),
    description: e.description || "",
    category: e.category || "Miscellaneous",
    amount: parseFloat(e.amount) || 0,
    date: e.date || "",
    vendor: e.vendor || "",
  };
}

export function normalizeSale(s) {
  return {
    id: s.id || Date.now(),
    client: s.client || "",
    description: s.description || "",
    amount: parseFloat(s.amount) || 0,
    date: s.date || "",
  };
}

export function normalizeSalary(s) {
  return {
    id: s.id || Date.now(),
    employee: s.employee || "",
    amount: parseFloat(s.amount) || 0,
    date: s.date || "",
    note: s.note || "",
  };
}

export function normalizeDebt(d) {
  return {
    id: d.id || Date.now(),
    vendor: d.vendor || "",
    note: d.note || "",
    totalAmount: parseFloat(d.totalAmount) || 0,
    remaining:
      d.remaining != null
        ? parseFloat(d.remaining)
        : parseFloat(d.totalAmount) || 0,
    interestRate: parseFloat(d.interestRate) || 0,
    dueDate: d.dueDate || "",
    addedDate: d.addedDate || "",
    payments: (d.payments || []).map((p) => ({
      date: p.date || "",
      amount: parseFloat(p.amount) || 0,
      fullClear: !!p.fullClear,
    })),
  };
}

export function normalizeOB(b) {
  return {
    date: b.date || "",
    amount: parseFloat(b.amount) || 0,
    note: b.note || "",
  };
}

// ─── Merge helpers ────────────────────────────────────────────────────────────
// Merge arrays deduplicating by id (a wins on conflict)
export function mergeById(a = [], b = []) {
  const ids = new Set(a.map((x) => x.id));
  return [...a, ...b.filter((x) => !ids.has(x.id))];
}

// Merge opening balances deduplicating by date (a wins)
export function mergeOB(a = [], b = []) {
  const dates = new Set(a.map((x) => x.date));
  return [...a, ...b.filter((x) => !dates.has(x.date))];
}

// ─── Parse any raw saved object into a clean v2 state ─────────────────────────
export function parseIntoV2(raw) {
  if (!raw || typeof raw !== "object") return null;
  return {
    _version: SCHEMA_VERSION,
    openingBalances: (raw.openingBalances || []).map(normalizeOB),
    purchases: (raw.purchases || []).map(normalizePurchase),
    expenses: (raw.expenses || []).map(normalizeExpense),
    sales: (raw.sales || []).map(normalizeSale),
    salaries: (raw.salaries || []).map(normalizeSalary),
    debts: (raw.debts || []).map(normalizeDebt),
  };
}

// ─── Load all storage keys, migrate, merge, save back — runs once on startup ─
export function loadAndMigrate() {
  // 1. Load current key
  let current = null;
  try {
    const r = localStorage.getItem(CURRENT_KEY);
    if (r) current = parseIntoV2(JSON.parse(r));
  } catch {}

  // 2. Load all legacy keys
  const legacySources = [];
  for (const key of LEGACY_KEYS) {
    try {
      const r = localStorage.getItem(key);
      if (r) {
        const p = parseIntoV2(JSON.parse(r));
        if (p) legacySources.push(p);
      }
    } catch {}
  }

  // 3. Merge — current wins, legacy fills any gaps
  let merged = current || { ...defaultState };
  for (const legacy of legacySources) {
    merged = {
      ...merged,
      purchases: mergeById(merged.purchases, legacy.purchases),
      expenses: mergeById(merged.expenses, legacy.expenses),
      sales: mergeById(merged.sales, legacy.sales),
      salaries: mergeById(merged.salaries, legacy.salaries),
      debts: mergeById(merged.debts, legacy.debts),
      openingBalances: mergeOB(merged.openingBalances, legacy.openingBalances),
      _version: SCHEMA_VERSION,
    };
  }

  // 4. Save merged result back to current key
  try {
    localStorage.setItem(CURRENT_KEY, JSON.stringify(merged));
  } catch {}

  // 5. Remove legacy keys now data is safely in current key
  if (legacySources.length > 0) {
    for (const key of LEGACY_KEYS) {
      try {
        localStorage.removeItem(key);
      } catch {}
    }
  }

  return merged;
}

// ─── Daily cash-flow engine ───────────────────────────────────────────────────
// For a given date, compute every cash movement and ending balance
export function computeDayFlow(date, data, prevEOD) {
  // Opening balance for this day = either manually set OR previous day's EOD
  const manualOB = (data.openingBalances || []).find((b) => b.date === date);
  const openingBalance = manualOB ? manualOB.amount : (prevEOD ?? 0);

  // CASH IN
  const salesIn = data.sales
    .filter((s) => s.date === date)
    .reduce((a, b) => a + b.amount, 0);

  // CASH OUT — immediate same-day
  const expensesOut = (data.expenses || [])
    .filter((e) => e.date === date)
    .reduce((a, b) => a + b.amount, 0);
  const salariesOut = data.salaries
    .filter((s) => s.date === date)
    .reduce((a, b) => a + b.amount, 0);
  const debtPaidOut = (data.debts || [])
    .flatMap((d) => (d.payments || []).filter((p) => p.date === date))
    .reduce((a, b) => a + b.amount, 0);

  // CASH OUT — vendor credit paid today (purchased yesterday)
  const vendorPaidOut = data.purchases
    .filter((p) => p.payDue === date && p.paid)
    .reduce((a, b) => a + b.amount, 0);

  // PENDING outflow (vendor credit due today but not yet paid)
  const vendorDuePending = data.purchases
    .filter((p) => p.payDue === date && !p.paid)
    .reduce((a, b) => a + b.amount, 0);

  const totalIn = salesIn;
  const totalOut = expensesOut + salariesOut + vendorPaidOut + debtPaidOut;
  const netPL = totalIn - totalOut;
  const eodBalance = openingBalance + netPL;

  // EOD balance if pending vendor also paid
  const eodIfAllPaid = eodBalance - vendorDuePending;

  return {
    date,
    openingBalance,
    manualOB: !!manualOB,
    salesIn,
    expensesOut,
    salariesOut,
    vendorPaidOut,
    debtPaidOut,
    vendorDuePending,
    totalIn,
    totalOut,
    netPL,
    eodBalance,
    eodIfAllPaid,
  };
}

// ─── Build chronological list of all dates that have any activity ─────────────
export function getAllActiveDates(data) {
  const dateSet = new Set();
  data.sales.forEach((s) => dateSet.add(s.date));
  (data.expenses || []).forEach((e) => dateSet.add(e.date));
  data.salaries.forEach((s) => dateSet.add(s.date));
  data.purchases.forEach((p) => {
    dateSet.add(p.purchaseDate);
    dateSet.add(p.payDue);
  });
  (data.openingBalances || []).forEach((b) => dateSet.add(b.date));
  (data.debts || [])
    .flatMap((d) => d.payments || [])
    .forEach((p) => dateSet.add(p.date));
  return [...dateSet].sort();
}

// ─── Build full daily summary chain (each day's opening = prev day's EOD) ─────
export function buildDailySummaries(data) {
  const dates = getAllActiveDates(data);
  if (!dates.length) return [];
  const summaries = [];
  let prevEOD = null;
  for (const date of dates) {
    const day = computeDayFlow(date, data, prevEOD);
    summaries.push(day);
    prevEOD = day.eodBalance;
  }
  return summaries;
}

// ─── Debt strategy engine ─────────────────────────────────────────────────────
export function computeDebtStrategy(debts, availableCash) {
  const today = todayStr();
  const active = (debts || [])
    .filter((d) => d.remaining > 0)
    .map((d) => ({
      ...d,
      overdueDays: d.dueDate ? Math.max(0, daysBetween(d.dueDate, today)) : 0,
      interestRate: d.interestRate || 0,
    }));
  if (!active.length) return { avalanche: [], snowball: [], suggested: [] };

  const avalanche = [...active].sort(
    (a, b) => b.interestRate - a.interestRate || b.overdueDays - a.overdueDays,
  );
  const snowball = [...active].sort(
    (a, b) => a.remaining - b.remaining || b.overdueDays - a.overdueDays,
  );
  const base = [...active].sort(
    (a, b) =>
      b.overdueDays - a.overdueDays ||
      b.interestRate - a.interestRate ||
      a.remaining - b.remaining,
  );

  let cash = availableCash || 0;
  const suggested = base.map((d) => {
    const pay = Math.min(cash, d.remaining);
    cash = Math.max(0, cash - pay);
    return { ...d, suggestedPayment: pay, fullyCleared: pay >= d.remaining };
  });

  return { avalanche, snowball, suggested };
}
